<template>
    <div class="comment-page">
        <!-- 工具条：标题说明 + 刷新 -->
        <div class="comment-toolbar">
            <div class="comment-toolbar__info">
                <span class="comment-toolbar__icon">
                    <el-icon>
                        <ChatLineSquare />
                    </el-icon>
                </span>
                <span class="comment-toolbar__meta">
                    <span class="comment-toolbar__title">互动评论</span>
                    <span class="comment-toolbar__subtitle">汇总你在文档中的评论与收到的回复，点击「查看」可直达对应文档</span>
                </span>
            </div>
            <div class="comment-toolbar__actions">
                <el-button class="toolbar-btn toolbar-btn--ghost" :loading="loading" @click="refreshAll">
                    <el-icon v-if="!loading" class="btn-icon">
                        <Refresh />
                    </el-icon>
                    刷新
                </el-button>
            </div>
        </div>

        <!-- 分类切换：我评论的 / 回复我的（带数量，代替原按钮组） -->
        <div class="comment-switch">
            <button type="button" class="comment-switch__item" :class="{ 'is-active': queryType === 'myCreated' }"
                @click="loadMyCreated">
                <el-icon class="comment-switch__icon">
                    <EditPen />
                </el-icon>
                <span>我评论的</span>
                <em class="comment-switch__count">{{ myCreatedList.length }}</em>
            </button>
            <button type="button" class="comment-switch__item" :class="{ 'is-active': queryType === 'replyMe' }"
                @click="loadReplyMe">
                <el-icon class="comment-switch__icon">
                    <ChatDotRound />
                </el-icon>
                <span>回复我的</span>
                <em class="comment-switch__count">{{ replyMeList.length }}</em>
            </button>
        </div>

        <!-- 搜索 + 统计 -->
        <div class="comment-filter">
            <el-input v-model="keyword" class="comment-search" placeholder="搜索文档标题、评论内容或人员" clearable>
                <template #prefix>
                    <el-icon>
                        <Search />
                    </el-icon>
                </template>
            </el-input>
            <span class="comment-filter__count">
                共 {{ filteredList.length }} 条{{ keyword ? '（已筛选）' : '' }}
            </span>
        </div>

        <!-- 评论列表 -->
        <section class="comment-panel">
            <el-empty v-if="filteredList.length === 0"
                :description="keyword ? '没有匹配的评论' : (queryType === 'myCreated' ? '你还没有发表过评论' : '还没有人回复你')" />
            <article class="comment-item" v-for="row in filteredList" :key="row.id">
                <span class="comment-item__avatar" :title="otherUserName(row)">{{ nameInitial(row) }}</span>

                <div class="comment-item__main">
                    <div class="comment-item__head">
                        <span class="comment-item__doc" :title="docTitle(row)" @click="previewDoc(row)">
                            <el-icon class="comment-item__doc-icon">
                                <Document />
                            </el-icon>
                            {{ docTitle(row) }}
                        </span>
                        <span class="comment-chip"
                            :class="row.isReplyUserRead ? 'comment-chip--read' : 'comment-chip--unread'">
                            <el-icon class="comment-chip__icon">
                                <component :is="row.isReplyUserRead ? 'CircleCheck' : 'BellFilled'" />
                            </el-icon>
                            {{ row.isReplyUserRead ? '已读' : '未读' }}
                        </span>
                    </div>

                    <div class="comment-item__content" :title="row.content">{{ row.content }}</div>

                    <div class="comment-item__meta">
                        <span class="comment-chip comment-chip--who">
                            {{ queryType === 'myCreated' ? '回复给' : '评论人' }} {{ otherUserName(row) }}
                        </span>
                        <span class="comment-item__time" :title="row.createTime">
                            {{ relativeLabel(row.createTime) }}
                        </span>
                    </div>
                </div>

                <div class="comment-item__actions">
                    <el-button class="view-btn" type="primary" plain @click="previewDoc(row)">查看</el-button>
                </div>
            </article>
        </section>
    </div>
</template>

<script>
import request from '../../../utils/request.js';

export default {
    data() {
        return {
            // myCreated：我评论的；replyMe：回复我的
            queryType: 'myCreated',
            keyword: '',
            loading: false,
            myCreatedList: [],
            replyMeList: []
        };
    },
    computed: {
        // 当前分类下的评论
        currentList() {
            return this.queryType === 'myCreated' ? this.myCreatedList : this.replyMeList;
        },
        // 关键字过滤（前端过滤，不改动接口返回的排序）
        filteredList() {
            const kw = this.keyword.trim().toLowerCase();
            if (!kw) return this.currentList;
            return this.currentList.filter(row => {
                const fields = [this.docTitle(row), row.content, this.otherUserName(row)];
                return fields.some(text => String(text || '').toLowerCase().includes(kw));
            });
        }
    },
    mounted() {
        this.refreshAll();
    },
    methods: {
        refreshAll() {
            this.loading = true;
            return Promise.all([this.loadMyCreated(), this.loadReplyMe()]).finally(() => {
                this.loading = false;
            });
        },
        loadMyCreated() {
            this.queryType = 'myCreated';
            return this.loadDocComment('myCreated', 'myCreatedList');
        },
        loadReplyMe() {
            this.queryType = 'replyMe';
            return this.loadDocComment('replyMe', 'replyMeList');
        },
        loadDocComment(type, listKey) {
            return request({
                url: '/doccomment/myList',
                method: 'post',
                data: {
                    type: type
                }
            }).then(res => {
                if (res.code == 0) {
                    this[listKey] = res.data || [];
                }
            });
        },
        // 对方：我评论的看「回复给谁」，回复我的看「谁评论的」
        otherUser(row) {
            return (this.queryType === 'myCreated' ? row.replyUser : row.createUser) || {};
        },
        otherUserName(row) {
            return this.otherUser(row).realName || '—';
        },
        nameInitial(row) {
            const name = this.otherUserName(row);
            return name && name !== '—' ? name.slice(0, 1) : '评';
        },
        docTitle(row) {
            return (row.docLite && row.docLite.title) || '未命名文档';
        },
        // 把时间格式化为「x 天前」这类相对描述
        relativeLabel(time) {
            if (!time) return '—';
            const date = new Date(String(time).replace(/-/g, '/').replace('T', ' '));
            if (isNaN(date.getTime())) return String(time);
            const diff = Date.now() - date.getTime();
            if (diff < 60 * 1000) return '刚刚';
            if (diff < 60 * 60 * 1000) return Math.floor(diff / 60000) + ' 分钟前';
            if (diff < 24 * 60 * 60 * 1000) return Math.floor(diff / 3600000) + ' 小时前';
            if (diff < 30 * 24 * 60 * 60 * 1000) return Math.floor(diff / 86400000) + ' 天前';
            return String(time).slice(0, 10);
        },
        // 查看：在新标签页打开评论所在文档
        previewDoc(row) {
            if (!row.docLite) return;
            let routeData = this.$router.resolve({
                name: 'collected',
                params: { collectedId: row.docLite.collectedId, docId: row.docLite.id }
            });
            window.open(routeData.path, '_blank');
        }
    }
};
</script>

<style scoped>
.comment-page {
    /* 局部覆盖主色，与其它工作台页面保持同一套视觉语言 */
    --el-color-primary: #2f6fed;
    --el-color-primary-light-3: #6d9af2;
    --el-color-primary-light-5: #97b7f6;
    --el-color-primary-light-7: #c1d4fa;
    --el-color-primary-light-8: #d5e2fb;
    --el-color-primary-light-9: #eaf1fd;
    --el-color-primary-dark-2: #2659be;

    --comment-text: #1f2d3d;
    --comment-sub: #5b6a80;
    --comment-muted: #aab4c4;
    --comment-line: #eef1f6;
    --comment-border: #e6eaf0;

    color: var(--comment-text);
}

/* ---------- 工具条 ---------- */
.comment-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    row-gap: 10px;
    padding: 10px 16px;
    border: 1px solid #ebeef5;
    border-radius: 12px;
    background: linear-gradient(180deg, #ffffff 0%, #fafbfd 100%);
    box-shadow: 0 10px 28px -22px rgba(31, 45, 61, 0.55);
}

.comment-toolbar__info {
    display: flex;
    align-items: center;
    min-width: 0;
}

.comment-toolbar__icon {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    margin-right: 10px;
    border-radius: 10px;
    font-size: 18px;
    color: #fff;
    background: linear-gradient(135deg, #6a9cff 0%, #2f6fed 100%);
    box-shadow: 0 8px 16px -10px rgba(47, 111, 237, 0.9);
}

.comment-toolbar__meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.comment-toolbar__title {
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: #1f2d3d;
}

.comment-toolbar__subtitle {
    font-size: 12px;
    line-height: 16px;
    color: #a8abb2;
}

.comment-toolbar__actions {
    display: flex;
    flex: none;
    flex-wrap: wrap;
    align-items: center;
    row-gap: 8px;
}

/* ---------- 工具条按钮 ---------- */
.toolbar-btn {
    height: 34px;
    padding: 0 16px;
    border-radius: 9px;
    font-weight: 500;
    transition: all 0.2s ease;
}

.toolbar-btn--ghost {
    color: #606266;
    background-color: #fff;
    border-color: #e4e7ed;
}

.toolbar-btn--ghost:hover,
.toolbar-btn--ghost:focus {
    color: var(--el-color-primary);
    background-color: var(--el-color-primary-light-9);
    border-color: var(--el-color-primary-light-5);
    transform: translateY(-1px);
    box-shadow: 0 10px 18px -12px rgba(47, 111, 237, 0.9);
}

.btn-icon {
    margin-right: 4px;
}

/* ---------- 分类切换 ---------- */
.comment-switch {
    display: flex;
    gap: 8px;
    margin-top: 14px;
}

.comment-switch__item {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 18px;
    border: 1px solid var(--comment-border);
    border-radius: 10px;
    background-color: #fff;
    color: var(--comment-sub);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.comment-switch__item:hover {
    color: var(--el-color-primary);
    border-color: var(--el-color-primary-light-5);
}

.comment-switch__item.is-active {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #2f6fed 0%, #2659be 100%);
    box-shadow: 0 10px 20px -12px rgba(47, 111, 237, 1);
}

.comment-switch__icon {
    font-size: 16px;
}

.comment-switch__count {
    padding: 0 8px;
    border-radius: 999px;
    background-color: #f2f5fa;
    color: var(--comment-sub);
    font-size: 12px;
    font-style: normal;
    line-height: 18px;
}

.comment-switch__item.is-active .comment-switch__count {
    background-color: rgba(255, 255, 255, 0.24);
    color: #fff;
}

/* ---------- 搜索与统计 ---------- */
.comment-filter {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
}

.comment-search {
    flex: 0 1 320px;
    min-width: 180px;
}

.comment-search :deep(.el-input__wrapper) {
    border-radius: 10px;
    background-color: #fff;
    box-shadow: 0 0 0 1px var(--comment-border) inset;
    transition: box-shadow 0.2s;
}

.comment-search :deep(.el-input__wrapper:hover) {
    box-shadow: 0 0 0 1px #cfd8e6 inset;
}

.comment-search :deep(.el-input__wrapper.is-focus) {
    box-shadow: 0 0 0 1px var(--el-color-primary) inset;
}

.comment-filter__count {
    font-size: 12px;
    color: var(--comment-muted);
}

/* ---------- 评论列表 ---------- */
.comment-panel {
    margin-top: 12px;
    padding: 6px;
    border: 1px solid var(--comment-border);
    border-radius: 14px;
    background-color: #fff;
    box-shadow: 0 1px 2px rgba(28, 47, 84, 0.05);
}

.comment-panel :deep(.el-empty) {
    padding: 40px 0;
}

.comment-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 14px;
    border-radius: 10px;
    transition: background-color 0.2s;
}

.comment-item+.comment-item {
    border-top: 1px solid var(--comment-line);
}

.comment-item:hover {
    background-color: #f7faff;
}

.comment-item__avatar {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    color: var(--el-color-primary);
    background-color: var(--el-color-primary-light-9);
}

.comment-item__main {
    flex: 1 1 auto;
    min-width: 0;
}

.comment-item__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}

.comment-item__doc {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 14px;
    font-weight: 600;
    color: var(--comment-text);
    cursor: pointer;
    transition: color 0.2s;
}

.comment-item__doc:hover {
    color: var(--el-color-primary);
}

.comment-item__doc-icon {
    flex: none;
    margin-right: 4px;
    font-size: 14px;
    color: var(--comment-muted);
}

.comment-item__content {
    margin-top: 4px;
    font-size: 13px;
    line-height: 20px;
    color: var(--comment-sub);
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    word-break: break-word;
}

.comment-item__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-top: 6px;
    font-size: 12px;
    color: var(--comment-muted);
}

.comment-item__time {
    white-space: nowrap;
}

.comment-item__actions {
    display: flex;
    flex: none;
    align-items: center;
    padding-top: 2px;
}

.view-btn {
    border-radius: 8px;
}

/* ---------- 状态标签 ---------- */
.comment-chip {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 1px 8px;
    border-radius: 999px;
    font-size: 12px;
    line-height: 18px;
    white-space: nowrap;
}

.comment-chip__icon {
    font-size: 12px;
}

.comment-chip--read {
    background-color: var(--el-color-primary-light-9);
    color: var(--el-color-primary);
}

.comment-chip--unread {
    background-color: #fff7e6;
    color: #e6a23c;
}

.comment-chip--who {
    background-color: #f2f5fa;
    color: var(--comment-sub);
}

/* ---------- 小尺寸设备适配 ---------- */
@media (max-width: 768px) {
    .comment-switch {
        width: 100%;
    }

    .comment-switch__item {
        flex: 1 1 0;
        justify-content: center;
        padding: 0 10px;
    }

    .comment-filter {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
    }

    .comment-search {
        flex: 1 1 auto;
        width: 100%;
    }

    /* 窄屏下每条评论改为「头像 + 内容」两行，操作按钮靠右收起 */
    .comment-item {
        flex-wrap: wrap;
        row-gap: 8px;
        padding: 12px;
    }

    .comment-item__actions {
        flex: 1 1 100%;
        justify-content: flex-end;
    }
}
</style>
